import { useMemo, useState } from "react";
import TurnoCard from "../../components/cards/TurnoCard";
import Modal from "../../components/modals/Modal";
import Toast from "../../components/toast/Toast";
import { IconCalendar, IconClock } from "../../components/icons/IconsPaciente";
import { EmptyState, FiltroEstado } from "../../components/utils/Utils";
import "../../components/toast/Toast.css";

const FILTROS_ESTADO = [
    { value: "todos", label: "Todos" },
    { value: "pendiente_confirmacion", label: "Pendiente de confirmación" },
    { value: "reservado", label: "Reservado" },
    { value: "confirmado", label: "Confirmado" },
    { value: "realizado", label: "Realizado" },
    { value: "cancelado", label: "Cancelado" },
];

const toDatetimeLocal = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatFechaLarga = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "fecha no disponible";
    return date.toLocaleString("es-AR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const MisTurnos = ({ turnos = [], loading, error, paciente, onCancelar, onProponerCambio, onAceptarCambio, onRechazarCambio }) => {
    const [toastError, setToastError] = useState("");
    const [editando, setEditando] = useState(null);
    const [nuevaFecha, setNuevaFecha] = useState("");
    const [guardando, setGuardando] = useState(false);
    const [filtroEstado, setFiltroEstado] = useState("todos");

    const turnosFiltrados = useMemo(() => {
        if (filtroEstado === "todos") return turnos;
        return turnos.filter((t) => t.estado === filtroEstado);
    }, [turnos, filtroEstado]);

    const { pendientes, proximos, historial } = useMemo(() => {
        return turnosFiltrados.reduce(
            (groups, turno) => {
                const fecha = new Date(turno.fechaHora);
                const esPasado = !Number.isNaN(fecha.getTime()) && fecha < new Date();
                const estadoFinal = ["realizado", "cancelado"].includes(turno.estado);

                if (turno.estado === "reservado" && !esPasado) {
                    groups.pendientes.push(turno);
                } else if (!estadoFinal && !esPasado) {
                    groups.proximos.push(turno);
                } else {
                    groups.historial.push(turno);
                }
                return groups;
            },
            { pendientes: [], proximos: [], historial: [] },
        );
    }, [turnosFiltrados]);

    const handleCancelar = async (turno) => {
        try {
            await onCancelar?.(turno);
        } catch (e) {
            setToastError(e.message);
        }
    };

    const abrirEdicion = (turno) => {
        setToastError("");
        setEditando(turno);
        setNuevaFecha(toDatetimeLocal(turno.fechaHora));
    };

    const guardarCambioFecha = async () => {
        if (!nuevaFecha) {
            setToastError("Seleccioná una fecha y hora.");
            return;
        }
        setGuardando(true);
        setToastError("");
        try {
            await onProponerCambio(editando, new Date(nuevaFecha).toISOString());
            setEditando(null);
        } catch (e) {
            setToastError(e.message);
        } finally {
            setGuardando(false);
        }
    };

    const handleAceptar = async (turno) => {
        setGuardando(true);
        try {
            await onAceptarCambio(turno);
            setEditando(null);
        } catch (e) {
            setToastError(e.message);
        } finally {
            setGuardando(false);
        }
    };

    const handleRechazar = async (turno) => {
        setGuardando(true);
        try {
            await onRechazarCambio(turno);
            setEditando(null);
        } catch (e) {
            setToastError(e.message);
        } finally {
            setGuardando(false);
        }
    };

    const esPendiente = editando?.estado === "pendiente_confirmacion";

    const renderTurnoCard = (t, conAcciones = false) => (
        <TurnoCard
            key={t.id}
            turno={t}
            proximos={conAcciones}
            onCancelar={handleCancelar}
            onEditar={abrirEdicion}
        />
    );

    return (
        <div className="patient-section">
            <div className="patient-section__header">
                <div>
                    <p className="eyebrow">Mi agenda</p>
                    <h2>Mis turnos</h2>
                </div>
            </div>

            <FiltroEstado options={FILTROS_ESTADO} value={filtroEstado} onChange={setFiltroEstado} />

            {error && <div className="patient-state patient-state--error">{error}</div>}

            {!paciente && !loading && !error && (
                <div className="patient-state patient-state--error">
                    No se encontró un paciente asociado al usuario logueado.
                </div>
            )}

            {loading ? (
                <div className="patient-state">
                    <div className="patient-state__loader" />
                    <p>Cargando turnos...</p>
                </div>
            ) : (
                <>
                    {pendientes.length > 0 && (
                        <div className="turnos-group">
                            <h3 className="turnos-group__title">Pendientes de confirmación · {pendientes.length}</h3>
                            <div className="turnos-list">
                                {pendientes.map((t) => renderTurnoCard(t, true))}
                            </div>
                        </div>
                    )}

                    <div className="turnos-group">
                        <h3 className="turnos-group__title">Próximos · {proximos.length}</h3>
                        {proximos.length === 0 ? (
                            <EmptyState
                                icon={<IconCalendar />}
                                title="No tenés turnos confirmados próximos."
                                subtitle='Reservá uno desde "Reservar turno".'
                            />
                        ) : (
                            <div className="turnos-list">
                                {proximos.map((t) => renderTurnoCard(t, true))}
                            </div>
                        )}
                    </div>

                    <div className="turnos-group">
                        <h3 className="turnos-group__title">Historial · {historial.length}</h3>
                        {historial.length === 0 ? (
                            <EmptyState icon={<IconClock />} title="No hay turnos en el historial." />
                        ) : (
                            <div className="turnos-list">
                                {historial.map((t) => <TurnoCard key={t.id} turno={t} />)}
                            </div>
                        )}
                    </div>
                </>
            )}

            {editando && (
                <Modal
                    title={esPendiente ? "Solicitud de cambio de fecha" : "Proponer cambio de fecha"}
                    onClose={() => setEditando(null)}
                >
                    <div className="modal-form">
                        {esPendiente ? (
                            editando.propuestoPor === "PACIENTE" ? (
                                <p className="modal-info">
                                    Propusiste cambiar este turno al <strong>{formatFechaLarga(editando.fechaHoraPropuesta)}</strong>. Esperando que el médico acepte o rechace.
                                </p>
                            ) : (
                                <>
                                    <p className="modal-info">
                                        El médico propuso cambiar este turno al <strong>{formatFechaLarga(editando.fechaHoraPropuesta)}</strong>. ¿Aceptás el nuevo horario?
                                    </p>
                                    <div className="modal-form__actions">
                                        <button className="btn-ghost btn-ghost--danger" onClick={() => handleRechazar(editando)} disabled={guardando}>
                                            Rechazar
                                        </button>
                                        <button className="primary-button" onClick={() => handleAceptar(editando)} disabled={guardando}>
                                            {guardando ? "Guardando..." : "Aceptar cambio"}
                                        </button>
                                    </div>
                                </>
                            )
                        ) : (
                            <>
                                <label className="form-field">
                                    <span>Nueva fecha y hora</span>
                                    <input
                                        type="datetime-local"
                                        value={nuevaFecha}
                                        onChange={(e) => setNuevaFecha(e.target.value)}
                                    />
                                </label>
                                <p className="modal-hint">El turno pasará a <strong>Pendiente de confirmación</strong> hasta que el médico acepte.</p>
                                <div className="modal-form__actions">
                                    <button className="btn-ghost" onClick={() => setEditando(null)}>
                                        Cancelar
                                    </button>
                                    <button className="primary-button" onClick={guardarCambioFecha} disabled={guardando}>
                                        {guardando ? "Enviando..." : "Proponer cambio"}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </Modal>
            )}

            <Toast message={toastError} onClose={() => setToastError("")} />
        </div>
    );
};

export default MisTurnos;